/// modules
import { BrowserModule } from '@angular/platform-browser';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { StoreModule } from '@ngrx/store';
import { reducers } from './root.reducer';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { AppRoutingModule } from './app.routing';
import { CoreModule } from './core/core.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CookieModule } from 'ngx-cookie';
import { EffectsModule } from '@ngrx/effects';
import { SharedModule } from './shared/shared.module';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ProfileModule } from './profile/profile.module';

/// components
import { AppComponent } from './app.component';
import { PageNotFoundComponent } from './notFound/pageNotFound.component';
import { OtpCallbackComponent } from './shared/services/otp/otp-callback.component';
import { MfaEnrollmentComponent } from './auth/components/mfa/enrollment/enrollment.component';
import { MfaEnrollmentCallbackComponent } from './auth/components/mfa/callback/callback.component';

// providers & services
import { ApiService } from './shared/services/api.service';
import { AppSettings, AppConstants } from './app.settings';
import { AuthModule } from '@tesla/ng-auth';
import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { HttpProgressInterceptor } from './shared/helper/http-progress-interceptor';
import { ForbiddenComponent } from './auth/components/forbidden.component';
import { UnauthorizedComponent } from './auth/components/unauthorized.component';
import { SpinnerService } from './shared/services/spinner.service';
import { OtpGuard } from './shared/services/otp/otp.guard';

import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeIt from '@angular/common/locales/it';
import localeja from '@angular/common/locales/ja';
import localeko from '@angular/common/locales/ko';
import localezh from '@angular/common/locales/zh';
import localecs from '@angular/common/locales/cs';
import localefi from '@angular/common/locales/fi';
import localehu from '@angular/common/locales/hu';
import localenn from '@angular/common/locales/nn';
import localepl from '@angular/common/locales/pl';
import localesv from '@angular/common/locales/sv';
import localeda from '@angular/common/locales/da';
import localeis from '@angular/common/locales/is';

import { ShellComponent } from './shell/shell.component';
import { ShellGuard } from './shell/shell.guard';

import { NgxMatomoTrackerModule } from '@ngx-matomo/tracker';
import { NgxMatomoRouterModule } from '@ngx-matomo/router';
import { SessionInvalidComponent } from './auth/components/mfa/session-invalid/session-invalid.component';

registerLocaleData(localeFr);
registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localeIt);
registerLocaleData(localeIt);
registerLocaleData(localeja);
registerLocaleData(localeFr);
registerLocaleData(localeko);
registerLocaleData(localezh);
registerLocaleData(localecs);
registerLocaleData(localefi);
registerLocaleData(localehu);
registerLocaleData(localenn);
registerLocaleData(localepl);
registerLocaleData(localesv);
registerLocaleData(localeda);
registerLocaleData(localeis);

export function appConstantsFactory(): AppConstants {
    const hostName = window.location.hostname;
    if (hostName.includes('dev')) {
        return (window as any).APP_DEV_CONSTANTS as AppConstants;
    } else if (hostName.includes('uat')) {
        return (window as any).APP_UAT_CONSTANTS as AppConstants;
    } else if (hostName.includes('stg2')) {
        return (window as any).APP_STG2_CONSTANTS as AppConstants;
    } else if (hostName.includes('stg')) {
        return (window as any).APP_STG_CONSTANTS as AppConstants;
    } else if (hostName.includes('localhost')) {
        return (window as any).APP_LOCAL_CONSTANTS as AppConstants;
    } else {
        return (window as any).APP_CONSTANTS as AppConstants;
    }
}

const appConstants = appConstantsFactory();

@NgModule({
    declarations: [
        AppComponent,
        PageNotFoundComponent,
        ForbiddenComponent,
        UnauthorizedComponent,
        OtpCallbackComponent,
        MfaEnrollmentComponent,
        SessionInvalidComponent,
        MfaEnrollmentCallbackComponent,
        ShellComponent,
    ],
    imports: [
        AuthModule.forRoot(appConstants.openid),
        BrowserModule,
        HttpClientModule,
        StoreModule.forRoot(reducers),
        CoreModule,
        SharedModule.forRoot(),
        BrowserAnimationsModule,
        AppRoutingModule,
        CookieModule.forRoot(),
        EffectsModule.forRoot([]), // we need emppty array  to initialize effects on feature level
        ProfileModule,
        CommonModule,
        NgxMatomoTrackerModule.forRoot(JSON.parse(appConstants.anlytics_config)),
        NgxMatomoRouterModule,
    ],
    providers: [
        ApiService,
        AppSettings,
        OtpGuard,
        SpinnerService,
        ShellGuard,
        { provide: LOCALE_ID, useValue: 'en-US' },
        { provide: APP_BASE_HREF, useValue: window['base-href'] },
        { provide: AppConstants, useFactory: appConstantsFactory },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpProgressInterceptor,
            multi: true,
        },
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
